import { Box, Text, VStack, HStack, Flex, Image, useColorModeValue } from '@chakra-ui/react';

export const ExportPreview = ({ config, pageType, logoUrl = null, selectedSections = [] }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const mutedColor = useColorModeValue('gray.400', 'gray.500');

  const { colors, branding, text } = config;
  const displayTitle = text.title || getDefaultTitle(pageType);
  const displaySubtitle = text.subtitle || getDefaultSubtitle(pageType);

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      p={4}
      border="1px solid"
      borderColor={borderColor}
      h="100%"
      minH="360px"
    >
      <Text fontSize="xs" color={mutedColor} mb={3} fontWeight="500" textTransform="uppercase" letterSpacing="wide">
        Preview
      </Text>

      {/* Mock document preview */}
      <Box
        bg={cardBg}
        borderRadius="lg"
        overflow="hidden"
        boxShadow="md"
        transform="scale(0.95)"
        transformOrigin="top"
      >
        {/* Header bar */}
        <Box bg={colors.primary} h="20px" position="relative">
          <Flex justify="space-between" align="center" px={3} h="100%">
            <HStack spacing={1}>
              {logoUrl && (
                <Box bg="white" borderRadius="2px" px="2px" py="1px">
                  <Image src={logoUrl} alt="Logo" h="10px" maxW="32px" objectFit="contain" />
                </Box>
              )}
              <Text fontSize="6px" color="white" fontWeight="bold">
                {branding.companyName || 'StatFalcon'}
              </Text>
            </HStack>
            <Text fontSize="5px" color="whiteAlpha.800">
              {pageType === 'reports' ? 'Ad Performance' : pageType === 'analytics' ? 'Web Analytics' : 'Dashboard'}
            </Text>
          </Flex>
        </Box>

        {/* Accent stripe */}
        <Box bg={colors.accent} h="3px" />

        {/* Content area */}
        <Box p={3}>
          {/* Title section */}
          <VStack align="flex-start" spacing={1} mb={3}>
            <Text fontSize="10px" fontWeight="bold" color={textColor}>
              {displayTitle}
            </Text>
            <Text fontSize="6px" color={mutedColor}>
              {displaySubtitle}
            </Text>
          </VStack>

          {/* Metric cards preview */}
          <HStack spacing={2} mb={3}>
            {[1, 2, 3, 4].map((i) => (
              <Box
                key={i}
                flex={1}
                bg={bgColor}
                borderRadius="md"
                p={2}
                borderLeft="2px solid"
                borderColor={i % 2 === 0 ? colors.accent : colors.primary}
              >
                <Box h="3px" bg={mutedColor} borderRadius="full" w="60%" mb={1} opacity={0.3} />
                <Box h="6px" bg={colors.primary} borderRadius="full" w="80%" opacity={0.5} />
              </Box>
            ))}
          </HStack>

          {/* Table header preview */}
          <Box mb={2}>
            <HStack
              bg={colors.primary}
              borderRadius="md"
              p={1.5}
              spacing={2}
            >
              {['Column 1', 'Column 2', 'Column 3', 'Column 4'].map((col, i) => (
                <Box key={i} flex={1}>
                  <Box h="4px" bg="white" borderRadius="full" opacity={0.8} w={i === 0 ? '80%' : '60%'} />
                </Box>
              ))}
            </HStack>
            {/* Table rows */}
            {[1, 2, 3].map((row) => (
              <HStack
                key={row}
                p={1.5}
                spacing={2}
                bg={row % 2 === 0 ? bgColor : 'transparent'}
              >
                {[1, 2, 3, 4].map((col) => (
                  <Box key={col} flex={1}>
                    <Box h="3px" bg={mutedColor} borderRadius="full" opacity={0.3} w={`${50 + col * 10}%`} />
                  </Box>
                ))}
              </HStack>
            ))}
          </Box>

          {/* Footer */}
          <Flex
            justify="space-between"
            align="center"
            pt={2}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <Text fontSize="5px" color={mutedColor}>
              {text.footerText || `Generated by ${branding.companyName || 'StatFalcon'}`}
            </Text>
            <Text fontSize="5px" color={mutedColor}>
              Page 1
            </Text>
          </Flex>
        </Box>
      </Box>

      {/* Selected sections */}
      {selectedSections.length > 0 && (
        <Box mt={3}>
          <Text fontSize="xs" color={mutedColor} mb={2} fontWeight="500" textTransform="uppercase" letterSpacing="wide">
            Sections ({selectedSections.length})
          </Text>
          <Flex wrap="wrap" gap={1.5}>
            {selectedSections.map((label) => (
              <Box
                key={label}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="full"
                px={2}
                py={0.5}
              >
                <Text fontSize="xs" color={textColor}>
                  {label}
                </Text>
              </Box>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

const getDefaultTitle = (pageType) => {
  const titles = {
    dashboard: 'Dashboard Report',
    analytics: 'Web Analytics Report',
    reports: 'Ad Performance Report',
  };
  return titles[pageType] || 'Report';
};

const getDefaultSubtitle = (pageType) => {
  const subtitles = {
    dashboard: 'Overview of key performance metrics',
    analytics: 'Traffic and engagement insights',
    reports: 'Campaign and ad analytics',
  };
  return subtitles[pageType] || 'Generated report';
};
